import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import axios from "axios";
import AddIcon from "@mui/icons-material/Add";
import MaterialTable from "../../components/Table/MaterialTable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { BASE_URL } from "../../utils/constants";

const Activities = () => {
  // Add state for grid view and form dialog
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    type: "success",
  });

  const [activityData, setActivityData] = useState({
    hotel_id: "",
    activity_name: "",
    activity_type: "",
    depend_on_room_type_yn: "0",
    paid_one_yn: "0",
    status: "1",
    description: "",
    customer_rating: "",
    seq_no: "",
  });
  const [activityTypes, setActivityTypes] = useState([]);
  const [allActivities, setAllActivities] = useState([]);
  const [activityNames, setActivityNames] = useState([]);
  const [isCustomName, setIsCustomName] = useState(false);

  // Add state for search icon
  const [searchIconClicked, setSearchIconClicked] = useState(false);

  // Define table columns
  const columns = [
    { title: "Activity ID", field: "activity_id" },
    { title: "Hotel ID", field: "hotel_id" },
    { title: "Activity Name", field: "activity_name" },
    { title: "Activity Type", field: "activity_type" },
    { title: "Depends on Room Type", field: "depend_on_room_type_yn" },
    { title: "Paid Activity", field: "paid_one_yn" },
    { title: "Description", field: "description" },
    { title: "Customer Rating", field: "customer_rating", type: "numeric" },
    { title: "Created At", field: "createdAt" },
    { title: "Updated At", field: "updatedAt" },
  ];

  // Handle export to Excel
  const handleExportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(allActivities);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Activities");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, `Activities_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  // Handle edit activity - trigger API call with 3 column values
  const handleEditActivity = async (rowData) => {
    console.log("Editing activity:", rowData);

    // Set basic data first
    setActivityData({
      hotel_id: rowData.hotel_id.toString(),
      activity_name: rowData.activity_name,
      activity_type: rowData.activity_type,
      depend_on_room_type_yn: "0",
      paid_one_yn: "0",
      status: "1",
      description: "",
      customer_rating: "",
    });

    setIsEditMode(true);
    setShowForm(true);

    // Trigger API call to fetch complete activity details
    try {
      const payload = {
        hotel_id: parseInt(rowData.hotel_id),
        activity_type: rowData.activity_type,
        activity_name: rowData.activity_name,
      };

      console.log("Fetching activity details for edit with payload:", payload);
      const response = await axios.post(
        `${BASE_URL}get_data/images_records/data`,
        payload,
      );
      console.log("API Response for edit:", response.data);

      if (response.data.success) {
        const data = response.data.data;

        // Clear previous images first
        setImages([]);
        setViewImages([]);

        const formattedData = {
          hotel_id: data.hotel_id.toString(),
          activity_name: data.activity_name,
          activity_type: data.activity_type,
          depend_on_room_type_yn:
            data.depend_on_room_type_yn === "YES" ? "1" : "0",
          paid_one_yn: data.paid_one_yn === "YES" ? "1" : "0",
          status: data.status ? data.status.toString() : "1",
          description: data.description || "",
          customer_rating: data.customer_rating?.toString() || "",
          activity_id: data.activity_id, // Store activity_id for update operations
        };

        // Set activity data from API response
        setActivityData(formattedData);

        // Store original data for comparison during update
        setOriginalActivityData(JSON.parse(JSON.stringify(formattedData)));

        // Set edit mode to true when we fetch activity details
        setIsEditMode(true);

        // Handle activity images
        let currentImages = [];
        if (data.image_id_from_image && data.image_path_from_image) {
          currentImages = [
            {
              preview: data.image_path_from_image,
              image_id: data.image_id_from_image,
              seq_no: data.seq_no || data.img_sequnce_no || "",
            },
          ];
          setImages(currentImages);
        }

        // Store original images for comparison
        setOriginalImages(JSON.parse(JSON.stringify(currentImages)));

        // Handle activity view images
        let currentViewImages = [];
        if (data.image_id_from_view && data.image_path_from_view) {
          currentViewImages = [
            {
              preview: data.image_path_from_view,
              image_id: data.image_id_from_view,
              seq_no: data.seq_no || data.img_sequnce_no || "",
            },
          ];
          setViewImages(currentViewImages);
        }

        // Store original view images for comparison
        setOriginalViewImages(JSON.parse(JSON.stringify(currentViewImages)));

        // Show success message
        setNotification({
          open: true,
          message: "Activity details loaded successfully for editing",
          type: "success",
        });
      } else {
        setNotification({
          open: true,
          message: "No activity found with the provided details",
          type: "warning",
        });
      }
    } catch (error) {
      console.error("Failed to fetch activity details for edit:", error);
      setNotification({
        open: true,
        message: "Failed to fetch activity details for editing",
        type: "error",
      });
    }
  };

  // Handle create new activity
  const handleCreateNew = () => {
    setActivityData({
      hotel_id: "",
      activity_name: "",
      activity_type: "",
      depend_on_room_type_yn: "0",
      paid_one_yn: "0",
      status: "1",
      description: "",
      customer_rating: "",
      seq_no: "",
    });
    setIsEditMode(false);
    setShowForm(true);
  };

  // Handle close form
  const handleCloseForm = () => {
    setShowForm(false);
    setIsEditMode(false);
    setActivityData({
      hotel_id: "",
      activity_name: "",
      activity_type: "",
      depend_on_room_type_yn: "0",
      paid_one_yn: "0",
      status: "1",
      description: "",
      customer_rating: "",
      seq_no: "",
    });
    setImages([]);
    setViewImages([]);
    setOriginalActivityData(null);
    setOriginalImages([]);
    setOriginalViewImages([]);
  };

  // Handle form submit success
  const handleFormSubmit = () => {
    setShowForm(false);
    setIsEditMode(false);
    // Refresh activities list
    fetchActivities();
    setNotification({
      open: true,
      message: isEditMode
        ? "Activity updated successfully"
        : "Activity created successfully",
      type: "success",
    });
  };

  // Handle close notification
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // Extract fetchActivities function to be reusable
  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${BASE_URL}HmActivity`);
      if (response.data?.data) {
        setAllActivities(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch activities:", error);
    }
  };

  // Update the useEffect for fetching activity types
  useEffect(() => {
    const fetchActivityTypes = async () => {
      try {
        const response = await axios.post(`${BASE_URL}get_metadata/data`, {
          metadata_type: "activity",
        });
        if (Array.isArray(response.data)) {
          setActivityTypes(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch activity types:", error);
      }
    };

    fetchActivityTypes();
  }, []);

  // Fetch all activities for dropdown - now uses the extracted function
  useEffect(() => {
    fetchActivities();
  }, []);

  // Filter activity names based on hotel_id and activity_type
  useEffect(() => {
    if (activityData.hotel_id && activityData.activity_type) {
      const filtered = allActivities
        .filter(
          (act) =>
            String(act.hotel_id) === String(activityData.hotel_id) &&
            act.activity_type === activityData.activity_type,
        )
        .map((act) => act.activity_name);

      // Use Set to remove duplicates and convert back to array
      setActivityNames([...new Set(filtered)]);

      // If the current activity name is not in the filtered list, reset it
      if (
        activityData.activity_name &&
        !filtered.includes(activityData.activity_name)
      ) {
        setActivityData((prev) => ({ ...prev, activity_name: "" }));
      }
    } else {
      setActivityNames([]);
    }
  }, [activityData.hotel_id, activityData.activity_type, allActivities]);

  const [showNewTypeField, setShowNewTypeField] = useState(false);
  const [newActivityType, setNewActivityType] = useState("");

  // Add this function to handle new activity type
  const handleAddActivityType = () => {
    if (newActivityType.trim()) {
      setActivityTypes([...activityTypes, newActivityType.trim()]);
      setNewActivityType("");
      setShowNewTypeField(false);
    }
  };

  const [hotels, setHotels] = useState([]);
  const [images, setImages] = useState([]);
  const [viewImages, setViewImages] = useState([]);
  const [activityImages, setActivityImages] = useState({
    is_active: "1",
  });

  // Fetch hotels
  useEffect(() => {
    const fetchHotels = async () => {
      try {
        const response = await axios.get(`${BASE_URL}Hotel`);
        if (response.data && Array.isArray(response.data.data)) {
          setHotels(response.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch hotels:", error);
      }
    };
    fetchHotels();
  }, []);

  // Handle image changes
  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    const MAX_SIZE_MB = 5; // limit 5 MB per image
    const MAX_WIDTH = 392;
    const MAX_HEIGHT = 392;

    const validatedImages = await Promise.all(
      files.map((file) => {
        return new Promise((resolve) => {
          const img = new Image();
          const objectUrl = URL.createObjectURL(file);
          img.onload = () => {
            const isValidSize = file.size / 1024 / 1024 <= MAX_SIZE_MB;
            const isValidDimensions =
              img.width <= MAX_WIDTH && img.height <= MAX_HEIGHT;

            if (!isValidSize) {
              alert(
                `"${file.name}" exceeds ${MAX_SIZE_MB} MB — please upload a smaller image.`,
              );
              resolve(null);
            } else if (!isValidDimensions) {
              alert(
                `"${file.name}" must be ${MAX_WIDTH}×${MAX_HEIGHT}px or smaller (current: ${img.width}×${img.height}).`,
              );
              resolve(null);
            } else {
              resolve({
                file,
                preview: objectUrl,
                seq_no: "",
                image_id: "",
              });
            }
          };
          img.onerror = () => {
            alert(`"${file.name}" could not be validated. Try again.`);
            resolve(null);
          };
          img.src = objectUrl;
        });
      }),
    );

    const validImages = validatedImages.filter(Boolean);
    setImages((prev) => [...prev, ...validImages]);
  };

  const handleViewImageChange = async (e) => {
    const files = Array.from(e.target.files);
    const MAX_SIZE_MB = 5;
    const MAX_WIDTH = 392;
    const MAX_HEIGHT = 392;

    const validatedImages = await Promise.all(
      files.map((file) => {
        return new Promise((resolve) => {
          const img = new Image();
          const objectUrl = URL.createObjectURL(file);
          img.onload = () => {
            const isValidSize = file.size / 1024 / 1024 <= MAX_SIZE_MB;
            const isValidDimensions =
              img.width <= MAX_WIDTH && img.height <= MAX_HEIGHT;

            if (!isValidSize) {
              alert(
                `"${file.name}" exceeds ${MAX_SIZE_MB} MB — please upload a smaller image.`,
              );
              resolve(null);
            } else if (!isValidDimensions) {
              alert(
                `"${file.name}" must be ${MAX_WIDTH}×${MAX_HEIGHT}px or smaller (current: ${img.width}×${img.height}).`,
              );
              resolve(null);
            } else {
              resolve({
                file,
                preview: objectUrl,
                seq_no: "",
                image_id: "",
              });
            }
          };
          img.onerror = () => {
            alert(`"${file.name}" could not be validated. Try again.`);
            resolve(null);
          };
          img.src = objectUrl;
        });
      }),
    );

    const validImages = validatedImages.filter(Boolean);
    setViewImages((prev) => [...prev, ...validImages]);
  };

  // Handle sequence changes
  const handleSequenceChange = (index, value) => {
    const updatedImages = [...images];
    updatedImages[index].seq_no = value;
    setImages(updatedImages);
  };

  const handleViewSequenceChange = (index, value) => {
    const updatedImages = [...viewImages];
    updatedImages[index].seq_no = value;
    setViewImages(updatedImages);
  };

  // Handle image removal
  const handleRemoveImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleViewRemoveImage = (index) => {
    setViewImages(viewImages.filter((_, i) => i !== index));
  };

  // Add fetchActivityDetails function after the existing useEffect hooks
  // Modify the fetchActivityDetails function to accept activity_name parameter
  // Modify the fetchActivityDetails function to properly handle data display
  // Add state to track original data for comparison
  const [originalActivityData, setOriginalActivityData] = useState(null);
  const [originalImages, setOriginalImages] = useState([]);
  const [originalViewImages, setOriginalViewImages] = useState([]);

  // Update fetchActivityDetails to properly handle the API response
  const fetchActivityDetails = async (
    hotel_id,
    activity_type,
    activity_name = null,
  ) => {
    try {
      const payload = {
        hotel_id: parseInt(hotel_id),
        activity_type: activity_type,
        activity_name: activity_name,
      };

      console.log("Fetching activity details with payload:", payload);
      const response = await axios.post(
        `${BASE_URL}get_data/images_records/data`,
        payload,
      );
      console.log("API Response:", response.data);

      if (response.data.success) {
        const data = response.data.data;

        // Clear previous images first
        setImages([]);
        setViewImages([]);

        const formattedData = {
          hotel_id: data.hotel_id.toString(),
          activity_name: data.activity_name,
          activity_type: data.activity_type,
          depend_on_room_type_yn:
            data.depend_on_room_type_yn === "YES" ? "1" : "0",
          paid_one_yn: data.paid_one_yn === "YES" ? "1" : "0",
          status: data.status ? data.status.toString() : "1",
          description: data.description || "",
          customer_rating: data.customer_rating?.toString() || "",
          seq_no: data.seq_no || data.img_sequnce_no || "",
          activity_id: data.activity_id, // Store activity_id for update operations
        };

        // Set activity data from API response
        setActivityData(formattedData);

        // Store original data for comparison during update
        setOriginalActivityData(JSON.parse(JSON.stringify(formattedData)));

        // Set edit mode to true when we fetch activity details
        setIsEditMode(true);

        // Handle activity images
        let currentImages = [];
        if (data.image_id_from_image && data.image_path_from_image) {
          currentImages = [
            {
              preview: data.image_path_from_image,
              image_id: data.image_id_from_image,
              seq_no: data.seq_no || data.img_sequnce_no || "",
            },
          ];
          setImages(currentImages);
        }

        // Store original images for comparison
        setOriginalImages(JSON.parse(JSON.stringify(currentImages)));

        // Handle activity view images
        let currentViewImages = [];
        if (data.image_id_from_view && data.image_path_from_view) {
          currentViewImages = [
            {
              preview: data.image_path_from_view,
              image_id: data.image_id_from_view,
              seq_no: data.seq_no || data.img_sequnce_no || "",
            },
          ];
          setViewImages(currentViewImages);
        }

        // Store original view images for comparison
        setOriginalViewImages(JSON.parse(JSON.stringify(currentViewImages)));

        // Show success message
        setNotification({
          open: true,
          message: "Activity details loaded successfully",
          type: "success",
        });
      } else {
        setNotification({
          open: true,
          message: "No activity found with the provided details",
          type: "warning",
        });
      }
    } catch (error) {
      console.error("Failed to fetch activity details:", error);
      setNotification({
        open: true,
        message: "Failed to fetch activity details",
        type: "error",
      });
    } finally {
      setSearchIconClicked(false);
    }
  };

  // Handle search icon click to fetch activity details
  const handleSearchClick = () => {
    if (
      activityData.hotel_id &&
      activityData.activity_type &&
      activityData.activity_name
    ) {
      setSearchIconClicked(true);
      fetchActivityDetails(
        activityData.hotel_id,
        activityData.activity_type,
        activityData.activity_name,
      );
    } else {
      setNotification({
        open: true,
        message:
          "Please fill in Hotel ID, Activity Type, and Activity Name before searching",
        type: "warning",
      });
    }
  };

  // Helper function to compare images
  const areImagesEqual = (images1, images2) => {
    if (images1.length !== images2.length) return false;

    for (let i = 0; i < images1.length; i++) {
      // If there's a new file, images have changed
      if (images1[i].file) return false;

      // Compare other properties
      if (
        images1[i].image_id !== images2[i].image_id ||
        images1[i].seq_no !== images2[i].seq_no
      ) {
        return false;
      }
    }

    return true;
  };

  // Modify handleSubmit to only send changed data and trigger relevant APIs
  const handleSubmit = async () => {
    try {
      // Validate required fields
      if (
        !activityData.hotel_id ||
        !activityData.activity_name ||
        !activityData.activity_type
      ) {
        alert("Please fill in all required fields");
        return;
      }

      let activity_id;
      let activityDataChanged = false;
      let imagesChanged = false;
      let viewImagesChanged = false;

      // For create mode, everything is considered changed
      if (!isEditMode) {
        activityDataChanged = true;
        imagesChanged = images.length > 0;
        viewImagesChanged = viewImages.length > 0;
      } else {
        // For edit mode, check what has changed
        if (originalActivityData) {
          activityDataChanged =
            activityData.depend_on_room_type_yn !==
              originalActivityData.depend_on_room_type_yn ||
            activityData.paid_one_yn !== originalActivityData.paid_one_yn ||
            activityData.status !== originalActivityData.status ||
            activityData.description !== originalActivityData.description ||
            activityData.customer_rating !==
              originalActivityData.customer_rating;
        }

        // Check if images have changed
        imagesChanged = !areImagesEqual(images, originalImages);

        // Check if view images have changed
        viewImagesChanged = !areImagesEqual(viewImages, originalViewImages);

        // If nothing has changed, show message and return
        // if (!activityDataChanged && !imagesChanged && !viewImagesChanged) {
        //   alert("No changes detected. Nothing to update.");
        //   return;
        // }
      }

      // Process activity data if it has changed or we're in create mode
      if (activityDataChanged || !isEditMode) {
        const activityPayload = {
          hotel_id: parseInt(activityData.hotel_id),
          activity_name: activityData.activity_name,
          activity_type: activityData.activity_type,
          depend_on_room_type_yn:
            activityData.depend_on_room_type_yn === "1" ? "YES" : "NO",
          paid_one_yn: activityData.paid_one_yn === "1" ? "YES" : "NO",
          status: parseInt(activityData.status),
          description: activityData.description,
          customer_rating: parseInt(activityData.customer_rating) || 0,
          seq_no: parseInt(activityData.seq_no) || 1,
        };

        if (!isEditMode) {
          // Create new activity
          const activityResponse = await axios.post(
            `${BASE_URL}create_activity/data_records`,
            activityPayload,
          );
          if (activityResponse.data.success) {
            activity_id = activityResponse.data.data.activity_id;
            console.log("Activity created with ID:", activity_id);
          } else {
            throw new Error("Failed to create activity");
          }
        } else {
          // Update existing activity
          activityPayload.activity_id = activityData.activity_id;
          const activityResponse = await axios.put(
            `${BASE_URL}update_activity/data_records/edit`,
            activityPayload,
          );
          activity_id = activityData.activity_id;
          console.log("Activity updated with ID:", activity_id);
        }
      } else {
        // If activity data didn't change but we need the ID for image operations
        activity_id = activityData.activity_id;
      }

      // Process images only if they have changed and we have a valid activity_id
      if (activity_id && imagesChanged) {
        // Handle activity images
        if (images.length > 0) {
          const formData = new FormData();
          const imageDataArray = images.map((img) => ({
            hotel_id: parseInt(activityData.hotel_id),
            activity_id: activity_id,
            image_id: img.image_id || undefined,
            seq_no: img.seq_no || "",
          }));

          formData.append("imageDataArray", JSON.stringify(imageDataArray));

          // Only append files that are new
          images.forEach((img) => {
            if (img.file) {
              formData.append("images", img.file);
            }
          });

          // Use appropriate endpoint based on operation
          const imageEndpoint = !isEditMode
            ? `${BASE_URL}upload-activity-images`
            : `${BASE_URL}update-activity-images`;

          const method = !isEditMode ? "post" : "put";
          console.log("Image Payload:", imageDataArray);
          await axios({
            method,
            url: imageEndpoint,
            data: formData,
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
        }
      }

      // Process view images only if they have changed and we have a valid activity_id
      if (activity_id && viewImagesChanged) {
        // Handle activity view images
        if (viewImages.length > 0) {
          const viewFormData = new FormData();
          const viewImageDataArray = viewImages.map((img) => ({
            hotel_id: parseInt(activityData.hotel_id),
            activity_id: activity_id,
            image_id: img.image_id || null,
            seq_no: img.seq_no || "",
          }));

          viewFormData.append(
            "imageDataArray",
            JSON.stringify(viewImageDataArray),
          );

          // Only append files that are new
          viewImages.forEach((img) => {
            if (img.file) {
              viewFormData.append("images", img.file);
            }
          });

          // Use appropriate endpoint based on operation
          const viewImageEndpoint = !isEditMode
            ? `${BASE_URL}upload-activity-images-views`
            : `${BASE_URL}update-activity-image-views`;

          const method = !isEditMode ? "post" : "put";
          console.log("View Image Payload:", viewImageDataArray);
          await axios({
            method,
            url: viewImageEndpoint,
            data: viewFormData,
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
        }
      }

      // Reset form after successful operation
      setActivityData({
        hotel_id: "",
        activity_name: "",
        activity_type: "",
        depend_on_room_type_yn: "0",
        paid_one_yn: "0",
        status: "1",
        description: "",
        customer_rating: "",
        seq_no: "",
      });
      setImages([]);
      setViewImages([]);
      setIsEditMode(false);
      setIsCustomName(false);
      setOriginalActivityData(null);
      setOriginalImages([]);
      setOriginalViewImages([]);

      // Show success message
      alert(
        isEditMode
          ? "Activity updated successfully"
          : "Activity created successfully",
      );

      // Refresh activities list
      fetchActivities();
    } catch (error) {
      console.error("Operation failed:", error);
      alert(
        `Failed to ${isEditMode ? "update" : "create"} activity: ${error.message}`,
      );
    }
  };

  // Update resetForm to clear original data
  const resetForm = () => {
    setActivityData({
      hotel_id: "",
      activity_name: "",
      activity_type: "",
      depend_on_room_type_yn: "0",
      paid_one_yn: "0",
      status: "1",
      description: "",
      customer_rating: "",
      seq_no: "",
    });
    setImages([]);
    setViewImages([]);
    setIsEditMode(false);
    setOriginalActivityData(null);
    setOriginalImages([]);
    setOriginalViewImages([]);
  };

  // Update the Save button text based on edit mode
  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h4" sx={{ color: "#1a237e", fontWeight: "bold" }}>
          Activities Management
        </Typography>
        <Button variant="outlined" onClick={handleExportToExcel}>
          Export to Excel
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <MaterialTable
          title="Activities"
          columns={columns}
          data={allActivities}
          selection={false}
          actions={false}
          onEdit={handleEditActivity}
          toolbar={{ create: true, onCreateClick: handleCreateNew }}
          sx={{
            "& .MuiPaper-root": { width: "100%", overflow: "hidden" },
            "& .MuiTableContainer-root": {
              maxHeight: "calc(100vh - 250px)",
              overflowX: "auto",
              overflowY: "auto",
            },
          }}
        />
      )}

      {/* Activity Form Dialog */}
      <Dialog open={showForm} onClose={handleCloseForm} maxWidth="lg" fullWidth>
        <DialogTitle>
          {isEditMode ? "Edit Activity" : "Create New Activity"}
        </DialogTitle>
        <DialogContent>
          {/* Activity Details Section */}
          <Box sx={{ mb: 4, mt: 2 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Activity Details
            </Typography>
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: "repeat(2, 1fr)",
              }}
            >
              <TextField
                select
                label="Hotel Name"
                value={activityData.hotel_id}
                onChange={(e) => {
                  setActivityData({
                    ...activityData,
                    hotel_id: e.target.value,
                  });
                }}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                disabled={isEditMode}
              >
                <option value="" disabled>
                  Select Hotel
                </option>
                {hotels
                  .filter((hotel) => hotel.is_active === true)
                  .map((hotel) => (
                    <option key={hotel.id} value={hotel.hotel_id}>
                      {hotel.name}
                    </option>
                  ))}
              </TextField>

              <TextField
                select
                label="Activity Type"
                value={activityData.activity_type}
                onChange={(e) => {
                  setActivityData({
                    ...activityData,
                    activity_type: e.target.value,
                  });
                }}
                fullWidth
                size="small"
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="">Select Activity Type</option>
                {activityTypes.map((type) => (
                  <option key={type.id} value={type.metadata_name}>
                    {type.metadata_name}
                  </option>
                ))}
              </TextField>

              {/* Activity Name Field - Now always a text field with search icon */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  label="Activity Name"
                  value={activityData.activity_name}
                  onChange={(e) => {
                    const newName = e.target.value;
                    setActivityData({
                      ...activityData,
                      activity_name: newName,
                    });

                    // If user types a new name, reset to create mode
                    if (newName && !isEditMode) {
                      setIsEditMode(false);
                    }
                  }}
                  fullWidth
                  size="small"
                  placeholder="Enter activity name and click search icon"
                />
                <IconButton
                  onClick={handleSearchClick}
                  color="primary"
                  size="small"
                  title="Search for existing activity"
                  disabled={
                    !activityData.hotel_id ||
                    !activityData.activity_type ||
                    !activityData.activity_name ||
                    searchIconClicked
                  }
                >
                  {searchIconClicked ? (
                    <CircularProgress size={20} />
                  ) : (
                    <AddIcon />
                  )}
                </IconButton>
              </Box>

              <TextField
                select
                label="Depends on Room Type"
                value={activityData.depend_on_room_type_yn}
                onChange={(e) =>
                  setActivityData({
                    ...activityData,
                    depend_on_room_type_yn: e.target.value,
                  })
                }
                SelectProps={{ native: true }}
                fullWidth
                size="small"
              >
                <option value="0">No</option>
                <option value="1">Yes</option>
              </TextField>

              <TextField
                select
                label="Paid Activity"
                value={activityData.paid_one_yn}
                onChange={(e) =>
                  setActivityData({
                    ...activityData,
                    paid_one_yn: e.target.value,
                  })
                }
                SelectProps={{ native: true }}
                fullWidth
                size="small"
              >
                <option value="0">No</option>
                <option value="1">Yes</option>
              </TextField>

              <TextField
                select
                label="Status"
                value={activityData.status}
                onChange={(e) =>
                  setActivityData({ ...activityData, status: e.target.value })
                }
                SelectProps={{ native: true }}
                fullWidth
                size="small"
              >
                <option value="0">Inactive</option>
                <option value="1">Active</option>
              </TextField>

              <TextField
                label="Description"
                value={activityData.description}
                onChange={(e) =>
                  setActivityData({
                    ...activityData,
                    description: e.target.value,
                  })
                }
                multiline
                rows={2}
                fullWidth
                size="small"
                sx={{ gridColumn: "1 / -1" }}
              />

              <TextField
                label="Customer Rating"
                type="number"
                value={activityData.customer_rating}
                onChange={(e) =>
                  setActivityData({
                    ...activityData,
                    customer_rating: e.target.value,
                  })
                }
                inputProps={{ min: 0, max: 5, step: 1 }}
                fullWidth
                size="small"
              />

              <TextField
                label="Sequence Number"
                type="number"
                value={activityData.seq_no}
                onChange={(e) =>
                  setActivityData({ ...activityData, seq_no: e.target.value })
                }
                inputProps={{ min: 1 }}
                fullWidth
                size="small"
              />
            </Box>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Activity Images Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Activity Images
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                />
              </Grid>
            </Grid>

            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Preview</TableCell>
                    <TableCell>Sequence No</TableCell>
                    <TableCell>Image ID</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {images.map((img, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <img
                          src={img.preview}
                          alt={`Preview ${index}`}
                          style={{
                            width: "100px",
                            height: "80px",
                            objectFit: "cover",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={img.seq_no}
                          onChange={(e) =>
                            handleSequenceChange(index, e.target.value)
                          }
                          sx={{ width: "100px" }}
                        />
                      </TableCell>
                      <TableCell>{img.image_id}</TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => handleRemoveImage(index)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Activity View Images Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Activity View Images
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleViewImageChange}
                />
              </Grid>
            </Grid>

            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Preview</TableCell>
                    <TableCell>Sequence No</TableCell>
                    <TableCell>Image ID</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {viewImages.map((img, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <img
                          src={img.preview}
                          alt={`Preview ${index}`}
                          style={{
                            width: "100px",
                            height: "80px",
                            objectFit: "cover",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={img.seq_no}
                          onChange={(e) =>
                            handleViewSequenceChange(index, e.target.value)
                          }
                          sx={{ width: "100px" }}
                        />
                      </TableCell>
                      <TableCell>{img.image_id}</TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => handleViewRemoveImage(index)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!activityData.hotel_id || !activityData.activity_name}
          >
            {isEditMode ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.type}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Activities;
